import { Injectable } from '@angular/core'
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects'
import {
    catchError,
    combineLatestWith,
    filter,
    forkJoin,
    map,
    mergeMap,
    of,
    switchMap,
    take,
    tap,
} from 'rxjs'
import { Store } from '@ngrx/store'
import { selectionActions, noOpAction } from './selection.actions'
import {
    selectSelectionType,
    selectFreeSelection,
    selectHierarchicalSelection,
    selectSavedFreeSelections,
    selectSavedHierarchicalSelections,
    selectSavedUnsupervisedSelections,
    selectUnsupervisedSelection,
    selectDisplayedSelectionsOnScatterPlot,
    selectSavedHierarchicalSelectionNodeItems,
    selectHierarchicalSelectionNodeItem,
    selectSavedUnsupervisedSelectionNodeItems,
} from './selection.selectors'
import { DatasetApiService } from '../../shared/services/dataset-api.service'
import { selectCaseId, selectLoadedSegSources } from '../case/case.selectors'
import { ToasterService } from '../../shared/services/toaster.service'
import { defaultDebounceTime } from '../../shared/utils/rxjs-pipelines'
import { RouterUtilsService } from '../../shared/services/router-utils.service'
import {
    HierarchicalSelectionNode,
    HierarchicalSelectionRoot,
    SelectionMode,
    SelectionType,
    UnsupervisedSelectionRoot,
    UnsupervisedSelectionNode,
    ensureBucketsForSegSources,
    getHierarchicalRootSegSources,
} from './selection.state'
import { viewerActions } from '../viewer/viewer.actions'
import { selectMarkerColor } from '../plot-data/plot-data.selectors'

@Injectable()
export class SelectionEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly datasetApi: DatasetApiService,
        private readonly toaster: ToasterService,
        private readonly routerUtils: RouterUtilsService
    ) {}

    loadFreeSelections$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(viewerActions.configLoaded),
            combineLatestWith(this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            switchMap(([, caseId]) => {
                return this.datasetApi.getFreeSelectionsForCase(caseId).pipe(
                    combineLatestWith(this.routerUtils.getFreeSelectionQueryParams()),
                    take(1),
                    map(([selections, queryParams]) => {
                        return selectionActions.loadingFreeSelectionsSucceeded({
                            selections,
                            queryParams,
                        })
                    }),
                    catchError(error =>
                        of(
                            selectionActions.loadingFreeSelectionsFailed({
                                error,
                                message: 'Error loading selections for dataset.',
                            })
                        )
                    )
                )
            })
        )
    })

    loadHierarchicalSelectionList$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(viewerActions.configLoaded),
            combineLatestWith(this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            switchMap(([, caseId]) =>
                this.datasetApi.getHierarchicalSelectionListForCase(caseId).pipe(
                    map(hierarchicalSelectionList =>
                        selectionActions.loadingHierarchicalSelectionListSucceeded({
                            list: hierarchicalSelectionList,
                        })
                    ),
                    catchError(error =>
                        of(
                            selectionActions.loadingHierarchicalSelectionListFailed({
                                error,
                                message: 'Error loading hierarchical selections for dataset.',
                            })
                        )
                    )
                )
            )
        )
    })

    loadUnsupervisedSelectionList$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(viewerActions.configLoaded),
            combineLatestWith(this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            switchMap(([, caseId]) =>
                this.datasetApi.getUnsupervisedSelectionListForCase(caseId).pipe(
                    map(unsupervisedSelectionList =>
                        selectionActions.loadingUnsupervisedSelectionListSucceeded({
                            list: unsupervisedSelectionList,
                        })
                    ),
                    catchError(error =>
                        of(
                            selectionActions.loadingUnsupervisedSelectionListFailed({
                                error,
                                message: 'Error loading unsupervised selections for dataset.',
                            })
                        )
                    )
                )
            )
        )
    })

    loadHierarchicalSelections$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.loadingHierarchicalSelectionListSucceeded),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            mergeMap(([{ list }, caseId]) => {
                if (list.length === 0) {
                    return of(noOpAction())
                }
                const apiCalls = list.map(name => {
                    return this.datasetApi.getSingleHierarchicalSelectionForCase(caseId, name).pipe(
                        take(1),
                        catchError(error =>
                            of(
                                selectionActions.loadingHierarchicalSelectionsFailed({
                                    error,
                                    message: 'Error loading hierarchical selection.',
                                })
                            )
                        )
                    )
                })
                return forkJoin(apiCalls).pipe(
                    combineLatestWith(this.routerUtils.getHierarchicalSelectionQueryParams()),
                    take(1),
                    map(([selections, queryParams]) => {
                        const combinedSelections = new Map<string, HierarchicalSelectionRoot>()
                        selections.forEach(selection => {
                            if ('error' in selection) {
                                return
                            }
                            for (const [key, value] of selection) {
                                combinedSelections.set(key, value)
                            }
                        })
                        return selectionActions.loadingHierarchicalSelectionsSucceeded({
                            selections: combinedSelections,
                            queryParams: queryParams,
                        })
                    })
                )
            })
        )
    })

    loadUnsupervisedSelections$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.loadingUnsupervisedSelectionListSucceeded),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            mergeMap(([{ list }, caseId]) => {
                if (list.length === 0) {
                    return of(noOpAction())
                }
                const apiCalls = list.map(name => {
                    return this.datasetApi.getSingleUnsupervisedSelectionForCase(caseId, name).pipe(
                        take(1),
                        catchError(error =>
                            of(
                                selectionActions.loadingUnsupervisedSelectionsFailed({
                                    error,
                                    message: 'Error loading unsupervised selection.',
                                })
                            )
                        )
                    )
                })
                return forkJoin(apiCalls).pipe(
                    combineLatestWith(this.routerUtils.getUnsupervisedSelectionQueryParams()),
                    take(1),
                    map(([selections, queryParams]) => {
                        const combinedSelections = new Map<string, HierarchicalSelectionRoot>()
                        selections.forEach(selection => {
                            if ('error' in selection) {
                                return
                            }
                            for (const [key, value] of selection) {
                                combinedSelections.set(key, value)
                            }
                        })
                        return selectionActions.loadingUnsupervisedSelectionsSucceeded({
                            selections: combinedSelections,
                            queryParams: queryParams,
                        })
                    })
                )
            })
        )
    })

    saveFreeSelection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.saveNewFreeSelection, selectionActions.saveFreeSelection),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            concatLatestFrom(() => [
                this.store.select(selectFreeSelection),
                this.store.select(selectLoadedSegSources),
            ]),
            switchMap(([[, caseId], selection, loadedSegSources]) => {
                const selectionToSave = {
                    ...selection,
                    buckets: ensureBucketsForSegSources(selection.buckets, loadedSegSources),
                }
                return this.datasetApi.saveFreeSelection(caseId!, selectionToSave).pipe(
                    map(() =>
                        selectionActions.savingFreeSelectionSucceeded({ selection: selectionToSave })
                    ),
                    catchError(error =>
                        of(
                            selectionActions.savingFreeSelectionFailed({
                                error,
                                message: 'Error saving selection.',
                            })
                        )
                    )
                )
            })
        )
    })

    saveHierarchicalSelectionRoot$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                selectionActions.saveNewHierarchicalSelectionRoot,
                selectionActions.saveNewHierarchicalSelectionNode,
                selectionActions.deleteHierarchicalSelectionNode,
                selectionActions.deleteHierarchicalSelectionNodeRecursively
            ),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            concatLatestFrom(() => this.store.select(selectHierarchicalSelection)),
            switchMap(([[, caseId], selection]) => {
                const selectionToSave = ensureTreeBucketsForLockedSegSources(selection)
                return this.datasetApi.saveHierarchicalSelection(caseId!, selectionToSave).pipe(
                    map(() =>
                        selectionActions.savingHierarchicalSelectionRootSucceeded({
                            selection: selectionToSave,
                        })
                    ),
                    catchError(error =>
                        of(
                            selectionActions.savingHierarchicalSelectionRootFailed({
                                error,
                                message: 'Error saving selection.',
                            })
                        )
                    )
                )
            })
        )
    })

    saveUnsupervisedSelectionRoot$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                selectionActions.saveNewAutoCellTypeDetectionRoot,
                selectionActions.saveNewAutoCellTypeDetectionNode,
                selectionActions.deleteAutoCellTypeDetectionNode,
                selectionActions.deleteAutoCellTypeDetectionNodeRecursively
            ),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            concatLatestFrom(() => this.store.select(selectUnsupervisedSelection)),
            switchMap(([[, caseId], selection]) => {
                const selectionToSave = ensureTreeBucketsForLockedSegSources(selection)
                return this.datasetApi.saveUnsupervisedSelection(caseId!, selectionToSave).pipe(
                    map(() =>
                        selectionActions.savingAutoCellTypeDetectionRootSucceeded({
                            selection: selectionToSave,
                        })
                    ),
                    catchError(error =>
                        of(
                            selectionActions.savingAutoCellTypeDetectionRootFailed({
                                error,
                                message: 'Error saving selection.',
                            })
                        )
                    )
                )
            })
        )
    })

    renameFreeSelection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.renameFreeSelection),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            switchMap(([{ name, newName }, caseId]) => {
                return this.datasetApi.renameFreeSelection(caseId!, name, newName).pipe(
                    map(() => selectionActions.renamingFreeSelectionSucceeded({ name, newName })),
                    catchError(error =>
                        of(
                            selectionActions.renamingFreeSelectionFailed({
                                error,
                                message: 'Error renaming selection.',
                            })
                        )
                    )
                )
            })
        )
    })

    renameHierarchicalSelectionRoot$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.renameHierarchicalSelectionRoot),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            switchMap(([{ name, newName }, caseId]) => {
                return this.datasetApi.renameHierarchicalSelectionRoot(caseId!, name, newName).pipe(
                    map(() =>
                        selectionActions.renamingHierarchicalSelectionRootSucceeded({
                            name,
                            newName,
                        })
                    ),
                    catchError(error =>
                        of(
                            selectionActions.renamingHierarchicalSelectionRootFailed({
                                error,
                                message: 'Error renaming selection.',
                            })
                        )
                    )
                )
            })
        )
    })

    renameHierarchicalSelection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.renameHierarchicalSelection),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            switchMap(([{ rootName, name, newName }, caseId]) => {
                return this.datasetApi
                    .renameHierarchicalSelection(caseId!, rootName, name, newName)
                    .pipe(
                        map(() =>
                            selectionActions.renamingHierarchicalSelectionSucceeded({
                                rootName,
                                name,
                                newName,
                            })
                        ),
                        catchError(error =>
                            of(
                                selectionActions.renamingHierarchicalSelectionFailed({
                                    error,
                                    message: 'Error renaming selection.',
                                })
                            )
                        )
                    )
            })
        )
    })

    renameUnsupervisedSelectionRoot$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.renameUnsupervisedSelectionRoot),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            switchMap(([{ name, newName }, caseId]) => {
                return this.datasetApi.renameUnsupervisedSelectionRoot(caseId!, name, newName).pipe(
                    map(() =>
                        selectionActions.renamingUnsupervisedSelectionRootSucceeded({
                            name,
                            newName,
                        })
                    ),
                    catchError(error =>
                        of(
                            selectionActions.renamingUnsupervisedSelectionRootFailed({
                                error,
                                message: 'Error renaming selection.',
                            })
                        )
                    )
                )
            })
        )
    })

    renameUnsupervisedSelection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.renameUnsupervisedSelection),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            switchMap(([{ rootName, name, newName }, caseId]) => {
                return this.datasetApi
                    .renameUnsupervisedSelection(caseId!, rootName, name, newName)
                    .pipe(
                        map(() =>
                            selectionActions.renamingUnsupervisedSelectionSucceeded({
                                rootName,
                                name,
                                newName,
                            })
                        ),
                        catchError(error =>
                            of(
                                selectionActions.renamingUnsupervisedSelectionFailed({
                                    error,
                                    message: 'Error renaming selection.',
                                })
                            )
                        )
                    )
            })
        )
    })

    deleteCurrentFreeSelection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.deleteFreeSelection),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            switchMap(([{ name }, caseId]) => {
                return this.datasetApi.deleteFreeSelection(caseId!, name).pipe(
                    map(() => selectionActions.deletingFreeSelectionSucceeded({ name })),
                    catchError(error =>
                        of(
                            selectionActions.deletingFreeSelectionFailed({
                                error,
                                message: 'Error deleting selection.',
                            })
                        )
                    )
                )
            })
        )
    })

    deleteCurrentHierarchicalSelectionRoot$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.deleteHierarchicalSelectionRoot),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            switchMap(([{ name }, caseId]) => {
                return this.datasetApi.deleteHierarchicalSelection(caseId!, name!).pipe(
                    map(() =>
                        selectionActions.deletingHierarchicalSelectionRootSucceeded({ name })
                    ),
                    catchError(error =>
                        of(
                            selectionActions.deletingHierarchicalSelectionRootFailed({
                                error,
                                message: 'Error deleting selection.',
                            })
                        )
                    )
                )
            })
        )
    })

    deleteCurrentAutoCellTypeDetectionRoot$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.deleteAutoCellTypeDetectionRoot),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            switchMap(([{ name }, caseId]) => {
                return this.datasetApi.deleteUnsupervisedSelection(caseId!, name!).pipe(
                    map(() =>
                        selectionActions.deletingAutoCellTypeDetectionRootSucceeded({ name })
                    ),
                    catchError(error =>
                        of(
                            selectionActions.deletingAutoCellTypeDetectionRootFailed({
                                error,
                                message: 'Error deleting selection.',
                            })
                        )
                    )
                )
            })
        )
    })

    showErrorToast$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    selectionActions.loadingFreeSelectionsFailed,
                    selectionActions.loadingHierarchicalSelectionListFailed,
                    selectionActions.loadingHierarchicalSelectionsFailed,
                    selectionActions.savingFreeSelectionFailed,
                    selectionActions.savingHierarchicalSelectionRootFailed,
                    selectionActions.deletingFreeSelectionFailed,
                    selectionActions.deletingHierarchicalSelectionRootFailed,
                    selectionActions.deletingAutoCellTypeDetectionRootFailed,
                    selectionActions.renamingHierarchicalSelectionRootFailed,
                    selectionActions.renamingHierarchicalSelectionFailed
                ),
                tap(({ error, message }) => {
                    this.toaster.httpError(error, message)
                })
            )
        },
        { dispatch: false }
    )

    // setQueryParamSelections$ = createEffect(
    //     () => {
    //         return this.actions$.pipe(
    //             ofType(
    //                 selectionActions.setSelectionOpacity,
    //                 selectionActions.setSelectionVisibility,
    //                 selectionActions.setSelectionShaderColor
    //             ),
    //             defaultDebounceTime(),
    //             concatLatestFrom(() => this.store.select(selectSavedFreeSelections)),
    //             concatLatestFrom(() => this.store.select(selectSavedHierarchicalSelections)),
    //             concatLatestFrom(() => this.store.select(selectSavedUnsupervisedSelections)),
    //             tap(([[[, selections], hierarchicalSelections], unsupervisedSelections]) => {
    //                 this.routerUtils.updateSelectionQueryParam(
    //                     selections,
    //                     hierarchicalSelections,
    //                     unsupervisedSelections
    //                 )
    //             })
    //         )
    //     },
    //     { dispatch: false }
    // )

    showSelectionOnSwitch$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.setFreeSelection),
            map(({ name }) => {
                return selectionActions.setSelectionVisibility({
                    selectionName: name,
                    visible: true,
                    selectionType: SelectionType.Free,
                    selectionRoot: undefined,
                })
            })
        )
    })

    resetSelectionMode$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                selectionActions.setFreeSelection,
                selectionActions.setHierarchicalSelectionNode,
                selectionActions.setHierarchicalSelectionRoot,
                selectionActions.setAutoCellTypeDetectionNode,
                selectionActions.setAutoCellTypeDetectionRoot,
                selectionActions.deleteFreeSelection,
                selectionActions.deleteHierarchicalSelectionNode,
                selectionActions.deleteHierarchicalSelectionRoot,
                selectionActions.deleteHierarchicalSelectionNodeRecursively,
                selectionActions.deleteAutoCellTypeDetectionNode,
                selectionActions.deleteAutoCellTypeDetectionNodeRecursively,
                selectionActions.deleteAutoCellTypeDetectionRoot,
                selectionActions.saveNewHierarchicalSelectionNode,
                selectionActions.saveNewHierarchicalSelectionRoot,
                selectionActions.saveNewFreeSelection,
                selectionActions.saveNewAutoCellTypeDetectionNode,
                selectionActions.saveNewAutoCellTypeDetectionRoot
            ),
            map(() => selectionActions.setMode({ mode: SelectionMode.Add }))
        )
    })

    setVisibleSelections$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                selectionActions.setHierarchicalSelectionNode,
                selectionActions.setHierarchicalSelectionRoot,
                selectionActions.setAutoCellTypeDetectionNode,
                selectionActions.setAutoCellTypeDetectionRoot,
                selectionActions.saveNewHierarchicalSelectionNode,
                selectionActions.saveNewAutoCellTypeDetectionNode,
                selectionActions.deleteHierarchicalSelectionNode,
                selectionActions.deleteAutoCellTypeDetectionNode,
                selectionActions.deleteHierarchicalSelectionNodeRecursively,
                selectionActions.deleteAutoCellTypeDetectionNodeRecursively
            ),
            concatLatestFrom(() => this.store.select(selectSelectionType)),
            concatLatestFrom(() => this.store.select(selectSavedHierarchicalSelectionNodeItems)),
            concatLatestFrom(() => this.store.select(selectSavedUnsupervisedSelectionNodeItems)),
            concatLatestFrom(() => this.store.select(selectHierarchicalSelectionNodeItem)),
            concatLatestFrom(() => this.store.select(selectMarkerColor)),
            map(
                ([
                    [
                        [
                            [[, selectionType], savedHierarchicalSelections],
                            savedUnsupervisedSelections,
                        ],
                        hierarchicalSelectionNodeItem,
                    ],
                    markerColor,
                ]) => {
                    if (markerColor) return noOpAction()
                    // Root / no active node: no membership coloring until a level is chosen.
                    if (!hierarchicalSelectionNodeItem?.name) {
                        return selectionActions.setVisibleSelectionsOnScatterPlot({
                            selectionNames: [],
                        })
                    }
                    const rval: string[] = [hierarchicalSelectionNodeItem.name]
                    let level = undefined

                    //get selections under the current selection
                    for (let s of selectionType == SelectionType.Hierarchical
                        ? savedHierarchicalSelections
                        : savedUnsupervisedSelections) {
                        if (level && s.level <= level) {
                            break
                        }
                        if (
                            s.name === hierarchicalSelectionNodeItem.name ||
                            (level && s.level > level)
                        ) {
                            rval.push(s.name!)
                            level = level || s.level
                        }
                    }
                    return selectionActions.setVisibleSelectionsOnScatterPlot({
                        selectionNames: rval,
                    })
                }
            )
        )
    })

    resetVisibleSelections$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                selectionActions.setFreeSelection,
                selectionActions.renameFreeSelection,
                selectionActions.deleteFreeSelection,
                selectionActions.deleteHierarchicalSelectionRoot,
                selectionActions.deleteAutoCellTypeDetectionRoot,
                selectionActions.saveNewHierarchicalSelectionRoot,
                selectionActions.saveNewFreeSelection,
                selectionActions.saveNewAutoCellTypeDetectionRoot
            ),
            concatLatestFrom(() => this.store.select(selectDisplayedSelectionsOnScatterPlot)),
            map(([, displayedSelections]) => {
                if (displayedSelections.length > 0) {
                    return selectionActions.setVisibleSelectionsOnScatterPlot({
                        selectionNames: [],
                    })
                } else {
                    return noOpAction()
                }
            })
        )
    })

    updateSelections$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    selectionActions.setSelectionOpacity,
                    selectionActions.setSelectionVisibility,
                    selectionActions.setSelectionShaderColor
                ),
                defaultDebounceTime(),
                this.datasetApi.saveViewerState(),
                filter(({ selectionName }) => !!selectionName),
                concatLatestFrom(() => this.store.select(selectCaseId)),
                filter(([, caseId]) => !!caseId),
                concatLatestFrom(() => this.store.select(selectSavedFreeSelections)),
                concatLatestFrom(() => this.store.select(selectSavedHierarchicalSelections)),
                concatLatestFrom(() => this.store.select(selectSavedUnsupervisedSelections)),
                switchMap(
                    ([
                        [[[action, caseId], freeSelections], hierarchicalSelections],
                        unsupervisedSelections,
                    ]) => {
                        if (action.selectionType == SelectionType.Free) {
                            if (!action.selectionName) return of()
                            const updatedSelection = freeSelections.get(action.selectionName)
                            if (!updatedSelection) return of()
                            return this.store.select(selectLoadedSegSources).pipe(
                                take(1),
                                switchMap(loadedSegSources =>
                                    this.datasetApi.saveFreeSelection(caseId!, {
                                        ...updatedSelection,
                                        buckets: ensureBucketsForSegSources(
                                            updatedSelection.buckets,
                                            loadedSegSources
                                        ),
                                    })
                                )
                            )
                        } else if (action.selectionType == SelectionType.Hierarchical) {
                            if (!action.selectionRoot) return of()
                            const updatedSelection = hierarchicalSelections.get(
                                action.selectionRoot
                            )
                            if (!updatedSelection) return of()
                            return this.datasetApi.saveHierarchicalSelection(
                                caseId!,
                                ensureTreeBucketsForLockedSegSources(updatedSelection)
                            )
                        } else if (action.selectionType == SelectionType.Unsupervised) {
                            if (!action.selectionRoot) return of()
                            const updatedSelection = unsupervisedSelections.get(
                                action.selectionRoot
                            )
                            if (!updatedSelection) return of()
                            return this.datasetApi.saveUnsupervisedSelection(
                                caseId!,
                                ensureTreeBucketsForLockedSegSources(updatedSelection)
                            )
                        } else {
                            return of()
                        }
                    }
                )
            )
        },
        { dispatch: false }
    )
}

function ensureNodeBucketsForSegSources(
    node: HierarchicalSelectionNode | UnsupervisedSelectionNode,
    segSources: string[]
): HierarchicalSelectionNode | UnsupervisedSelectionNode {
    return {
        ...node,
        buckets: ensureBucketsForSegSources(node.buckets, segSources),
        children: node.children?.map(child => ensureNodeBucketsForSegSources(child, segSources)),
    }
}

function ensureTreeBucketsForLockedSegSources<
    T extends HierarchicalSelectionRoot | UnsupervisedSelectionRoot,
>(root: T): T {
    const segSources = getHierarchicalRootSegSources(root)
    return {
        ...root,
        children: root.children?.map(child => ensureNodeBucketsForSegSources(child, segSources)),
    }
}
